import { Center, Flex, Loader } from "@mantine/core";
import { type ReactNode, useCallback, useEffect, useMemo } from "react";

import { useSmartOfferMessages } from "@/components/chat/ChatSmartOffer/useSmartOfferMessages";
import { CustomerPanelWrapper } from "@/customer-panel/CustomerPanelWrapper";
import { Panels } from "@/panels";
import { ProductSaleFlowPanel } from "@/sales-tools/product-sale-flow/ProductSaleFlowPanel";
import { useProductSaleFlow } from "@/sales-tools/product-sale-flow/useProductSaleFlow";
import { getSessionByTaskId, useChatTasks, useLogger } from "@/sdk";
import { type ChatTask, selectChatRightBottomTab, useChatStore } from "@/sdk/agent/chat";
import { useWrapupTimer } from "@/sdk/sessions/hooks";
import { useSessionStore } from "@/sdk/sessions/session.store";
import { useFeatureFlag } from "@/shared-utils";
import { PlatformTimeline } from "@/timeline";
import { useSetupChatGaiaSubscriptions } from "@/timeline/state";

import ChatControlsBar from "./ChatControls/ChatControlsBar";
import { ChatPanelTimelineView } from "./ChatPanelTimelineView";
import { ChatQueue } from "./ChatQueue";

/** provides a consistent layout for panel slots */
function SlotPanel({ children, ...props }: { children: ReactNode }) {
    return (
        <Flex h="100%" pos="absolute" style={{ containerType: "inline-size" }} inset={0} {...props}>
            {children}
        </Flex>
    );
}

export function ChatManager() {
    const logger = useLogger({ module: "ChatManager", supportTeam: "messaging" });
    const chatTasks = useChatTasks();

    const activeChatId = useChatStore((state) => state.activeChatId);
    const setActiveChatId = useChatStore((state) => state.setActiveChatId);
    const clearActiveChatId = useChatStore((state) => state.clearActiveChatId);
    const savedRightBottomTab = useChatStore(selectChatRightBottomTab(activeChatId));
    const { scheduleWrappingForTasks } = useWrapupTimer();
    const { selectedProduct, productSaleFlowPanelTitle, handleProductSaleClose } = useProductSaleFlow();

    /** activeChatTask memo derived directly from activeChatId and chatTasks */
    const activeChatTask = useMemo(
        () => chatTasks.find((task) => task.chatId === activeChatId),
        [activeChatId, chatTasks],
    );

    // Clear the activeChatId when no matching task is found
    useEffect(() => {
        if (activeChatId && !activeChatTask) {
            clearActiveChatId();
        }
    }, [activeChatId, activeChatTask, clearActiveChatId]);

    const onChatSelected = useCallback(
        (chat: ChatTask) => {
            if (chat.status === "pending") {
                logger.warn({ chatId: chat.chatId, taskId: chat.id }, "Attempted to select a pending chat");
                return;
            }

            const session = getSessionByTaskId(chat.id);
            if (session) {
                useSessionStore.getState().setSelectedSession(session.id);
            }
            setActiveChatId(chat.chatId);
        },
        [setActiveChatId, logger],
    );

    const isSelectedChat = useCallback(({ chatId }: ChatTask) => activeChatId === chatId, [activeChatId]);

    useEffect(() => {
        scheduleWrappingForTasks(chatTasks);
    }, [chatTasks, scheduleWrappingForTasks]);

    const { data: isSalesEnabled = false } = useFeatureFlag<boolean>("EWP-chat-isSalesEnabled", {
        partner: activeChatTask?.partner,
        topic: activeChatTask?.topic,
        language: activeChatTask?.language,
    });
    // TODO: Once we scale to experts, utilize the isLoading state from the useFeatureFlag hook to render the loading spinner while the feature flag is being fetched.
    const { data: isChatExpertAssistEnabled = false } = useFeatureFlag("isChatExpertAssistEnabled", {
        partner: activeChatTask?.partner,
    });
    const { data: isChatSmartOfferFlowEnabled = false, isLoading: isSmartOfferFlagLoading } = useFeatureFlag<boolean>(
        "EWP-chat-isSmartOfferFlowEnabled",
        { partner: activeChatTask?.partner },
    );
    const canShowChatDetails = activeChatTask && activeChatTask.status !== "pending";
    const shouldShowSalesPanel = canShowChatDetails && isSalesEnabled;

    useSetupChatGaiaSubscriptions(logger);

    const shouldShowQueue = chatTasks.length > 0;

    // Smart offer messages hook - manages sending messages to timeline
    useSmartOfferMessages(selectedProduct, isChatSmartOfferFlowEnabled);

    // Right bottom panel configuration
    // When smart offer is active, messages go to main timeline and Flows tab is hidden
    // When smart offer is disabled, show Flows tab if there's a product
    const shouldShowAssistTab = canShowChatDetails && isChatExpertAssistEnabled;
    const shouldShowFlowsTab = canShowChatDetails && selectedProduct && !isChatSmartOfferFlowEnabled;
    const isRightBottomLoading = canShowChatDetails && isSmartOfferFlagLoading;

    // Resolve the per-chat tab selection. If the user has not made a choice on this chat,
    // default to Flows when a product exists, otherwise Assist. If their saved choice is
    // Flows but no product is available, fall back to Assist (Flows tab is closed).
    const rightBottomSelectedTab = !shouldShowFlowsTab ? "a" : (savedRightBottomTab ?? "b");

    const handleRightBottomTabChange = useCallback((tab: "a" | "b") => {
        const currentChatId = useChatStore.getState().activeChatId;
        if (currentChatId) {
            useChatStore.getState().setChatRightBottomTab(currentChatId, tab);
        }
    }, []);

    return (
        <Panels
            leftMainPanelDefaultSize={60}
            leftSidebarSlot={
                shouldShowQueue && (
                    <SlotPanel>
                        <ChatQueue chats={chatTasks} isSelectedChat={isSelectedChat} onChatSelected={onChatSelected} />
                    </SlotPanel>
                )
            }
            leftSidebarConfig={{
                isCollapsed: !shouldShowQueue,
            }}
            topbarSlot={
                canShowChatDetails && (
                    <SlotPanel>
                        <ChatControlsBar chatTask={activeChatTask} />
                    </SlotPanel>
                )
            }
            topbarConfig={{
                isCollapsed: !canShowChatDetails,
                minHeight: "70px",
                maxHeight: "70px",
            }}
            rightSidebarSlot={
                shouldShowSalesPanel && (
                    <SlotPanel>
                        <CustomerPanelWrapper />
                    </SlotPanel>
                )
            }
            rightSidebarConfig={{
                isCollapsed: !shouldShowSalesPanel,
            }}
            leftTopASlot={<ChatPanelTimelineView activeChatTask={activeChatTask} />}
            leftTopConfig={{
                a: {
                    headerTitle: "Chat",
                    tabTitle: "Chat",
                    isMinimized: false,
                    isClosed: false,
                    showHeader: false,
                },
                b: {
                    headerTitle: "Tab B",
                    tabTitle: "Tab B",
                    isMinimized: false,
                    isClosed: true,
                    showHeader: false,
                },
            }}
            rightBottomASlot={
                isRightBottomLoading ? (
                    <SlotPanel>
                        <Center h="100%" w="100%">
                            <Loader />
                        </Center>
                    </SlotPanel>
                ) : (
                    shouldShowAssistTab && (
                        <SlotPanel>
                            <PlatformTimeline />
                        </SlotPanel>
                    )
                )
            }
            rightBottomBSlot={!isRightBottomLoading && shouldShowFlowsTab && <ProductSaleFlowPanel />}
            rightBottomConfig={{
                selectedTab: rightBottomSelectedTab,
                onTabChange: handleRightBottomTabChange,
                a: {
                    headerTitle: "Assist",
                    tabTitle: "Assist",
                    isMinimized: false,
                    isClosed: !(shouldShowAssistTab || isRightBottomLoading),
                    showHeader: false,
                },
                b: {
                    headerTitle: productSaleFlowPanelTitle,
                    tabTitle: "Flows",
                    isMinimized: false,
                    isClosed: !shouldShowFlowsTab,
                    showHeader: true,
                    onClose: handleProductSaleClose,
                },
            }}
        />
    );
}
